import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as IgnoreIcon } from '@/assets/icons/ignore.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import {
  CoverTh,
  MessageTh,
} from '@/pages/EditorialDistribution/components/EditorialDistributionTable';
import {
  EditorialFacebookPostResource,
  EditorialQueuePage,
  SortState,
} from '@/types/resources';
import { api } from '@/utils/api';
import { chunk } from '@/utils/array';
import { SortDirection } from '@/utils/enums';

import ArchivedPostTableRow from './ArchivedPostTableRow';
import AutoDeleteSwitch from './AutoDeleteSwitch';
import ClicksThresholdInput from './ClicksThresholdInput';
import MoreActions from './MoreActions';
import PageGuideButton from './PageGuideButton';
import PostsStatusNav from './PostsStatusNav';
import { tableColumns } from './PostsTable';

const Header = styled.div`
  display: flex;
  margin: 16px 0 24px;
  align-items: flex-end;
  justify-content: space-between;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Badge = styled(Text)`
  display: flex;
  align-items: center;
  height: 18px;
  padding: 0 9px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.gray};
  margin-right: 12px;
`;

const Wrap = styled.div`
  margin-top: 48px;
`;

export const CheckboxHolder = styled.div`
  position: relative;
`;

export const CheckboxLabel = styled.label`
  display: block;
  height: 16px;
  width: 16px;
  right: 10px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purpleLighter};
  }
`;

export const Checkbox = styled.input<{ $shouldUncheck?: boolean }>`
  display: none;
  &:checked + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: transparent;
    &:after {
      content: '';
      position: absolute;
      left: 5px;
      top: 2px;
      width: 5px;
      height: 9px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      ${({ $shouldUncheck }) =>
        $shouldUncheck &&
        css`
          background-color: ${({ theme }) => theme.colors.white};
          border-radius: 20px;
          width: 10px;
          height: 2px;
          border: 0;
          border-width: 0;
          transform: rotate(0deg);
          top: 0;
          left: 0;
          right: 2px;
          bottom: 0;
          margin: auto;
        `}
    }
  }
`;

const StyledTable = styled(Table)`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    min-width: 140px;
    vertical-align: top;
    &:first-child {
      width: 54px;
      min-width: 54px;
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:last-child {
      min-width: 56px;
      width: 56px;
      z-index: 1;
      position: relative;
    }
  }
  td {
    &:last-child {
      min-width: 56px;
      width: 56px;
      position: sticky;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 1;
      padding-right: 16px;
      padding-left: 16px;
      border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
  }
`;

const BulkButtonsHolder = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  right: 0;
  left: 0;
  margin: 0 auto;
  z-index: 10;
  width: 500px;
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const BulkActionButton = styled.button<{ $danger?: boolean }>`
  height: 56px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  background: ${({ theme }) => theme.colors.grayscaleLight};

  &:hover {
    border-color: ${({ theme, $danger }) =>
      theme.colors[$danger ? 'error' : 'black']};

    > * {
      color: ${({ theme, $danger }) =>
        theme.colors[$danger ? 'error' : 'black']};
    }
  }
`;

const Actions = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const StyledViewOptionsModal = styled(ViewOptionsModal)`
  margin: 0 !important;
`;

const DEFAULT_SORT_STATE = {
  direction: SortDirection.DESC,
  property: 'publishedAt',
};

interface Props {
  search: string;
  activePage: EditorialQueuePage;
}

const ArchiveTable: FC<Props> = ({ search, activePage }) => {
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);
  const [checkedPosts, setCheckedPosts] = useState(new Set<number>());
  const [network, networkLabel] = useNetwork();
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const { status } = useParams();

  const tableViewOptionsKey = `${status!}_${networkLabel}_dq_posts_table`;

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const fetchPosts = () => {
    return api.get<never, EditorialFacebookPostResource[]>(
      '/v1/editorial/facebook-posts',
      {
        params: {
          ...(sort.direction !== 'DEFAULT' && {
            sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
          }),
          ...(!!search && {
            'filter[search]': search,
          }),
          'filter[network]': network,
          'filter[queueStatus]': '2',
          'filter[channelId]': activePage.id,
          'filter[deletable]': 1,
          'filter[excludePlaceholders]': 1,
          withoutPagination: 1,
          include: 'departments',
        },
      },
    );
  };

  const {
    data: schedules,
    isError,
    isLoading,
  } = useQuery<EditorialFacebookPostResource[]>(
    [
      'editorial_distribution_schedules',
      {
        network,
        queueable: true,
        status: 'posted',
        search,
        deletable: 1,
        selectedChannels: activePage.id.toString(),
        withoutPlaceholders: true,
      },
    ],
    fetchPosts,
    {
      refetchInterval: 60 * 1000,
    },
  );

  const onCheckAll = () => {
    setCheckedPosts(
      (prev) => new Set(prev.size ? [] : schedules?.map((s) => s.id) ?? []),
    );
  };

  const onCheck = (postId: number) => {
    setCheckedPosts((prev) => {
      const newSet = new Set(prev);
      newSet.has(postId) ? newSet.delete(postId) : newSet.add(postId);
      return newSet;
    });
  };

  const onBulkDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Delete ${checkedPosts.size} selected posts`,
      );

      if (!confirmed) {
        return;
      }

      for (const batch of chunk(Array.from(checkedPosts), 50)) {
        await api.post(`/v1/editorial/facebook-posts/bulk-action`, {
          posts: batch,
          action: 'delete',
        });
      }

      setCheckedPosts(new Set());

      await queryClient.invalidateQueries(
        ['editorial_distribution_schedules'],
        {
          exact: false,
        },
      );

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  const onBulkIgnore = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Ignore ${checkedPosts.size} selected posts`,
      );

      if (!confirmed) {
        return;
      }

      for (const batch of chunk(Array.from(checkedPosts), 50)) {
        await api.post(`/v1/editorial/facebook-posts/bulk-action`, {
          posts: batch,
          action: 'ignore',
        });
      }

      setCheckedPosts(new Set());

      await queryClient.invalidateQueries(
        ['editorial_distribution_schedules'],
        {
          exact: false,
        },
      );

      alerts.close();
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <Wrap>
      <Header>
        <PostsStatusNav />
        <Actions>
          <AutoDeleteSwitch activePage={activePage} />
          <ClicksThresholdInput activePage={activePage} />
          <StyledViewOptionsModal
            columns={tableColumns}
            tableKey={tableViewOptionsKey}
          />
          <PageGuideButton activePage={activePage} />
          <MoreActions activePage={activePage} />
        </Actions>
      </Header>
      <Heading $color="gray" $type="smallHeadline" as="div">
        <Badge as="div" $type="extraSmall" $color="white">
          {schedules?.length ?? 0}
        </Badge>
        {activePage.name}
      </Heading>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>
                <CheckboxHolder>
                  <Checkbox
                    id="all-deletable-schedules"
                    type="checkbox"
                    checked={
                      !!schedules?.length &&
                      schedules?.length === checkedPosts.size
                    }
                    onChange={onCheckAll}
                  />
                  <CheckboxLabel htmlFor="all-deletable-schedules" />
                </CheckboxHolder>
              </Th>
              {getColumnVisibility('Cover') && <CoverTh>Cover</CoverTh>}
              {getColumnVisibility('Message') && <MessageTh>Message</MessageTh>}
              {getColumnVisibility('Network') && <Th>Network</Th>}
              {getColumnVisibility('Page Name') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="channelName"
                  title="Page Name"
                />
              )}
              {getColumnVisibility('Content Type') && <Th>Content Type</Th>}
              {getColumnVisibility('Status') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="status"
                  title="Status"
                />
              )}
              {getColumnVisibility('Benchmark Reach') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="benchmarkReach"
                  title="Benchmark Reach"
                />
              )}
              {getColumnVisibility('Deletion Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="deletedAt"
                  title="Deletion Date"
                />
              )}
              {getColumnVisibility('Schedule Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="createdAt"
                  title="Schedule Date"
                />
              )}
              {getColumnVisibility('Post Date') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="publishedAt"
                  title="Post Date"
                />
              )}
              {getColumnVisibility('Site Visits') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisits"
                  title="Site Visits"
                />
              )}
              {getColumnVisibility('Site Visit Benchmark') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="siteVisitBenchmark"
                  title="Site Visit Benchmark"
                />
              )}
              {getColumnVisibility('Reach Duration') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reachDuration"
                  title="Reach Duration"
                />
              )}
              {getColumnVisibility('Departments') && <Th>Departments</Th>}
              {getColumnVisibility('Reactions') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="reactions"
                  title="Reactions"
                />
              )}
              {getColumnVisibility('Comments') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="comments"
                  title="Comments"
                />
              )}
              {getColumnVisibility('Shares') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="shares"
                  title="Shares"
                />
              )}
              {getColumnVisibility('Article Link') && <Th>Article Link</Th>}
              {getColumnVisibility('Post Link') && <Th>Post Link</Th>}
              {getColumnVisibility('Owner') && (
                <SortableTh
                  setSort={setSort}
                  sort={sort}
                  property="owner"
                  title="Owner"
                />
              )}
              {getColumnVisibility('Notes') && <Th>Notes</Th>}
              {getColumnVisibility('Error Message') && <Th>Error Message</Th>}
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!schedules?.length}
            >
              {schedules?.map((schedule) => (
                <ArchivedPostTableRow
                  key={schedule.id}
                  schedule={schedule}
                  onCheck={onCheck}
                  isChecked={checkedPosts.has(schedule.id)}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {!!checkedPosts.size && (
          <BulkButtonsHolder
            initial={{ scale: 0, opacity: 0.3 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0.3 }}
          >
            <BulkActionButton $danger onClick={onBulkDelete}>
              <TrashIcon />
              <Text $type="caption" as="span">
                Delete selected
              </Text>
            </BulkActionButton>
            <BulkActionButton onClick={onBulkIgnore}>
              <IgnoreIcon />
              <Text $type="caption" as="span">
                Ignore selected
              </Text>
            </BulkActionButton>
          </BulkButtonsHolder>
        )}
      </AnimatePresence>
    </Wrap>
  );
};

export default ArchiveTable;
